import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ArrowLeftIcon } from '../../../../assets/icons/arrow-left.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit-pencil.svg';
import DefalutDashboard from '../../../../assets/images/dashboard/defalut-dashboard.png';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchCustomDashboards,
  updateCustomDashboard,
} from '../../../../redux/slices/CustomDashboardSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../../shared-components/button/Button';
import CustomCheckbox from '../../../../shared-components/custom-check-box/CustomCheckBox';
import CustomInputSwitch from '../../../../shared-components/custom-input-switch/CustomInputSwitch';
import DeleteWarning from '../../../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import { permissionCheck } from '../../../../shared-functions/JWTDocodeFunction';
import dashboardApi from '../../../../utils/api/custom-dashboard';

/**
 * Customize Dashboard view matching the provided UI screenshot.
 * @returns {any} The rendered component.
 */
function CustomizeDashboard(): JSX.Element {
  const { t, i18n } = useTranslation('dashboard');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { dashboards, defaultDashboard, loading } = useAppSelector(
    (state) => state.customDashboard,
  );
  const isRtl = i18n.dir() === 'rtl';

  /**
   * Sets a specific layout as the default dashboard.
   * @param {string} id - The ID of the layout.
   * @param {boolean} value - Whether the layout is set as default.
   */
  const handleSetDefault = (id: string, value: boolean) => {
    dispatch(updateCustomDashboard({ id, data: { isDefault: value } }));
  };

  /**
   * Toggles the "Show as a Tab" property for a layout.
   * @param {string} id - The ID of the layout.
   * @param {boolean} value - Whether the layout should be shown as a tab.
   */
  const handleToggleTab = (id: string, value: boolean) => {
    dispatch(updateCustomDashboard({ id, data: { showAsTab: value } }));
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState<{ id: string; name: string } | null>(null);

  /**
   * Navigates back to the main dashboard.
   */
  const handleBack = () => {
    navigate('/ev-admin/dashboard');
  };

  /**
   * Handles keyboard navigation on the back arrow.
   * @param {React.KeyboardEvent<SVGSVGElement>} event - The keyboard event.
   */
  const handleBackKeyDown = (event: React.KeyboardEvent<SVGSVGElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleBack();
    }
  };

  /**
   * Navigates to the create custom dashboard page.
   */
  const handleCreateDashboardClick = () => {
    navigate('/ev-admin/create-custom-dashboard');
  };

  /**
   * Navigates to the edit custom dashboard page.
   * @param {string} layoutId - The ID of the layout to edit.
   */
  const handleEditDashboard = (layoutId: string) => {
    navigate(`/ev-admin/edit-custom-dashboard/${layoutId}`);
  };

  /**
   * Opens the delete confirmation modal.
   * @param {string} id - The ID of the dashboard to delete.
   * @param {string} name - The name of the dashboard.
   */
  const handleDeleteClick = (id: string, name: string) => {
    setSelectedLayout({ id, name });
    setIsDeleteModalOpen(true);
  };

  /**
   * Closes the delete confirmation modal.
   */
  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  /**
   * Handles the actual deletion after confirmation.
   * @returns {Promise<void>} A promise that resolves when deletion is finished.
   */
  const handleConfirmDelete = async (): Promise<void> => {
    if (selectedLayout) {
      try {
        await dashboardApi.deleteCustomDashboard(selectedLayout.id);
        setIsDeleteModalOpen(false);
        dispatch(fetchCustomDashboards({}));
      } catch (error) {
        console.error('Error deleting dashboard:', error);
      }
    }
  };

  useEffect(() => {
    dispatch(fetchCustomDashboards({}));
  }, []);

  const allLayouts = defaultDashboard ? [defaultDashboard, ...dashboards] : [...dashboards];

  return (
    <div className={`customize-dashboard-wrap ${isRtl ? 'rtl' : ''}`} dir={i18n.dir()}>
      <div className="customize-header">
        <div className="header-left">
          <ArrowLeftIcon
            className="back-arrow"
            onClick={handleBack}
            role="button"
            tabIndex={0}
            aria-label="Back"
            onKeyDown={handleBackKeyDown}
          />
          <div className="customize-title">
            <span>{t('customizeDashboard')}</span>
          </div>
        </div>
        <div className="header-right">
          <MyButton
            label={t('createDashboard')}
            buttonType="submit"
            onClickFunc={handleCreateDashboardClick}
            disableButton={!permissionCheck('cpoCustomizeDashboardAdd')}
          />
        </div>
      </div>

      <div className="customize-description">
        <div className="description-content">
          <h2>{t('customizeYourDashboard')}</h2>
          <p>{t('customizeDashboardDescription')}</p>
        </div>
      </div>
      <div className="layout-cards-grid">
        {loading ? (
          <BatteryLoader />
        ) : (
          allLayouts.map((layout) => {
            const isDefaultLayout = layout.id === 'default';
            const layoutDisplayName = isDefaultLayout ? t('defaultDashboard') : layout.name;

            return (
              <div key={layout.id} className={`layout-card ${layout.isDefault ? 'active' : ''}`}>
                <div className="card-preview">
                  {layout.signedUrl ? (
                    <img
                      src={layout?.signedUrl || DefalutDashboard}
                      alt={layoutDisplayName}
                      className="dashboard-preview-img"
                    />
                  ) : (
                    <div className="preview-skeleton">
                      <div className="skeleton-row header" />
                      <div className="skeleton-row content" />
                      <div className="skeleton-row content sub" />
                    </div>
                  )}
                </div>

                <div className="card-info">
                  <div className="info-top">
                    <span className="layout-name">{layoutDisplayName}</span>
                    <span className="updated-at">{layout.updatedAt}</span>
                  </div>

                  <div className="info-bottom">
                    <div className="default-toggle">
                      <CustomInputSwitch
                        name={`default-${layout.id}`}
                        inputLabel={t('setAsDefault')}
                        value={layout.isDefault}
                        onChangeValue={(_, value) => handleSetDefault(layout.id, value)}
                        fontSize="small"
                      />
                    </div>

                    <div className="action-icons">
                      {!isDefaultLayout && (
                        <>
                          <EditIcon
                            className="icon-btn edit"
                            onClick={() => handleEditDashboard(layout.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(event: React.KeyboardEvent<SVGSVGElement>) =>
                              event.key === 'Enter' && handleEditDashboard(layout.id)
                            }
                          />
                          <DeleteIcon
                            className="icon-btn delete"
                            onClick={() => handleDeleteClick(layout.id, layout.name)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(event: React.KeyboardEvent<SVGSVGElement>) =>
                              event.key === 'Enter' && handleDeleteClick(layout.id, layout.name)
                            }
                          />
                        </>
                      )}
                    </div>
                  </div>

                  {!isDefaultLayout && (
                    <div className="tab-selection">
                      <CustomCheckbox
                        name={`tab-${layout.id}`}
                        checkboxLabel={t('showAsTabInMainDashboard')}
                        value={layout?.showAsTab || false}
                        onChangeValue={(_, value) => handleToggleTab(layout.id, value)}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <ModalBox
        open={isDeleteModalOpen}
        closeModal={handleCloseDeleteModal}
        title={t('deleteDashboard')}
        header={false}
        rightCorner=""
        content={
          <DeleteWarning
            selectedRowId={selectedLayout?.id}
            title={t('deleteDashboard')}
            description={t('deleteDashboardConfirm', {
              name:
                (selectedLayout?.id === 'default' ? t('defaultDashboard') : selectedLayout?.name) ||
                t('thisDashboard'),
            })}
            cancelButtonOnClick={handleCloseDeleteModal}
            submitButtonOnClick={handleConfirmDelete}
            submitSvgIcon={<DeleteIcon />}
          />
        }
      />
    </div>
  );
}

export default CustomizeDashboard;
